import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";
import { FormikHelpers, useFormik } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { transformToFormattedDate } from "~/OnboardingApp/utils";
import { optionalDate } from "~/utils/validations";
import { getDueDateCopyKeys } from "./utils";

type Values = {
  dueDate: string;
};

const validationSchema = Yup.object({
  dueDate: optionalDate,
});

export const DueDatePage: FC = () => {
  const { t } = useTranslation();
  const { dueDate, lengthOfTimeTtc } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const copyKeys = getDueDateCopyKeys(lengthOfTimeTtc);
  const initialDueDate = dueDate
    ? DateTime.fromISO(dueDate).toFormat("MM / dd / yyyy")
    : "";

  const initialValues: Values = {
    dueDate: initialDueDate || "",
  };

  const handleSubmit = (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    const { dueDate } = values;
    const date = DateTime.fromFormat(dueDate, "MM / dd / yyyy");
    const formattedDate = (date.isValid ? date.toISO() : "") as string;

    onSubmit({ dueDate: formattedDate }, formikHelpers);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const oldValue = formik.values.dueDate;

    const formattedDate = transformToFormattedDate({ value, oldValue });

    formik.setFieldValue("dueDate", formattedDate, true);
  };

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            description={t(copyKeys.description)}
            question={t(copyKeys.question)}
          />

          <StringInput
            id="dueDate"
            onChange={handleDateChange}
            placeholder={t("onboarding.dueDatePage.placeholder")}
          />
        </QuestionContainer>

        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
